import { getPaymentHistories } from "@endpoints/stripe.service";
import { ChevronDown, ListFilter, LogOut, SearchIcon, X } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@components/ui/popover";
import type { DateRange as CalendarDateRange } from "react-day-picker";
import { useCallback, useEffect, useRef, useState } from "react";
import dayjs from "@lib/dayjs";
import LoaderPaymentReceived from "@pages/referralAmbassador/Payment/components/LoaderPaymentReceived";
import { useDispatch } from "react-redux";
import { getEnumKeyFromValue } from "@utils/helperFunction";
import { NavLink } from "react-router-dom";
import { EnumPaymentHistoryTypes } from "@utils/constants";
import LoadingAnimation from "@assets/svg/LoadingAnimation";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@components/ui/dropdown-menu";
import { Calendar } from "@components/ui/calendar";

type DateRange = { startDate: string; endDate: string } | null;

interface IDateFilterProps {
  range: DateRange;
  setRange: (value: DateRange) => void;
}

const DateFilter = ({ range, setRange }: IDateFilterProps) => {
  const [open, setOpen] = useState(false);

  // Committed (string) range -> react-day-picker's Date-based range.
  const committed: CalendarDateRange | undefined = range?.startDate
    ? {
        from: new Date(range.startDate),
        to: range.endDate ? new Date(range.endDate) : undefined,
      }
    : undefined;

  // Local selection so the calendar can show an in-progress range (only `from`
  // picked) before both ends are chosen.
  const [tempRange, setTempRange] = useState<CalendarDateRange | undefined>(
    committed,
  );

  // Re-sync when the committed range changes/clears from outside.
  useEffect(() => {
    setTempRange(
      range?.startDate
        ? {
            from: new Date(range.startDate),
            to: range.endDate ? new Date(range.endDate) : undefined,
          }
        : undefined,
    );
  }, [range?.startDate, range?.endDate]);

  const label = range?.startDate
    ? `${dayjs(range.startDate).format("DD MMM")} - ${
        range.endDate ? dayjs(range.endDate).format("DD MMM") : "…"
      }`
    : "Date Filter";

  const handleSelect = (next?: CalendarDateRange) => {
    setTempRange(next);
    // Commit and close only once a full range is chosen.
    if (next?.from && next?.to) {
      setRange({
        startDate: dayjs(next.from).format("YYYY-MM-DD"),
        endDate: dayjs(next.to).format("YYYY-MM-DD"),
      });
      setOpen(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div className="bg-white rounded-md px-3 border flex items-center h-8 gap-3 cursor-pointer whitespace-nowrap">
          <span className="font-medium text-xs text-[#4D4D4D]">{label}</span>
          {range?.startDate ? (
            <X
              className="w-4 h-4"
              color="gray"
              onClick={(e) => {
                e.stopPropagation();
                setRange(null);
              }}
            />
          ) : (
            <ListFilter className="w-4 h-4" color="gray" />
          )}
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 bg-white" align="end">
        <Calendar
          mode="range"
          numberOfMonths={2}
          defaultMonth={tempRange?.from}
          selected={tempRange}
          onSelect={handleSelect}
          disabled={{ after: new Date() }}
          classNames={{
            // This project's Tailwind theme has no `accent` token, so paint the
            // range with the existing `primary` color instead (light tint for the
            // span; the start/end day buttons already render solid `bg-primary`).
            range_start: "rounded-l-md bg-primary/10",
            range_middle: "rounded-none bg-primary/10",
            range_end: "rounded-r-md bg-primary/10",
            today: "text-primary font-semibold",
          }}
        />
      </PopoverContent>
    </Popover>
  );
};

interface IPaymentTypeFilterProps {
  type: EnumPaymentHistoryTypes | null;
  setType: (value: EnumPaymentHistoryTypes | null) => void;
}

const PaymentTypeFilter = ({ type, setType }: IPaymentTypeFilterProps) => {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="bg-white rounded-md px-3 border flex items-center h-8 gap-6 z-10">
        <span className="font-medium text-xs text-[#4D4D4D]">
          {type ? getEnumKeyFromValue(EnumPaymentHistoryTypes, type) : "All"}
        </span>
        <ChevronDown className="w-4 h-4" color="gray" />
      </DropdownMenuTrigger>
      <DropdownMenuContent className="bg-white">
        <DropdownMenuItem
          onClick={() => setType(null)}
          className="hover:bg-gray-100 rounded-full font-medium text-xs cursor-pointer"
        >
          All
        </DropdownMenuItem>
        {Object.entries(EnumPaymentHistoryTypes).map(([label, value]) => (
          <DropdownMenuItem
            key={value}
            onClick={() => setType(value)}
            className="hover:bg-gray-100 rounded-full font-medium text-xs cursor-pointer"
          >
            {label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <div className="bg-white rounded-md px-3 border flex items-center h-8 gap-10">
      <span className="font-medium text-xs text-[#4D4D4D]">Type</span>
      <ChevronDown className="w-4 h-4" color="gray" />
    </div>
  );
};

const PaymentsSection = () => {
  const dispatch = useDispatch();
  const initialized = useRef(false);
  const scrollListRef = useRef(null);
  const sentinelRef = useRef(null);

  const [paymentHistories, setPaymentHistories] = useState([]);

  const [type, setType] = useState<null | EnumPaymentHistoryTypes>(null);
  const [dateRange, setDateRange] = useState<null | {
    startDate: string;
    endDate: string;
  }>(null);
  const [searchText, setSearchText] = useState<string | undefined>();
  const [debouncedSearchText, setDebouncedSearchText] = useState<
    string | undefined
  >();

  const [page, setPage] = useState(1);
  const [hasMoreData, setHasMoreData] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMoreData, setIsLoadingMoreData] = useState(false);

  // Debounce the search input and restart paging from page 1 on a new query.
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(searchText);
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchText]);

  const fetchPaymentHistories = useCallback(async () => {
    try {
      if (page === 1) setIsLoading(true);
      const { success, data } = await getPaymentHistories({
        page,
        searchText: debouncedSearchText,
        type: type ?? undefined,
        startDate: dateRange?.startDate,
        endDate: dateRange?.endDate,
      });
      if (success) {
        setHasMoreData(data?.hasMore);
        setPaymentHistories((prev) =>
          page > 1
            ? [...prev, ...(data?.paymentHistories || [])]
            : data?.paymentHistories || [],
        );
      }
    } catch (error) {
      console.error(`Error fetching payment histories`, error);
    } finally {
      setIsLoading(false);
      setIsLoadingMoreData(false);
    }
  }, [page, debouncedSearchText, type, dateRange]);

  // Filter setters reset paging to page 1 in the same update so a filter change
  // triggers exactly one page-1 fetch (no stale page-N request racing it).
  const applyType = useCallback((next: EnumPaymentHistoryTypes | null) => {
    setType(next);
    setPage(1);
  }, []);

  const applyDateRange = useCallback((next: DateRange) => {
    setDateRange(next);
    setPage(1);
  }, []);

  const handleLoadMore = useCallback(() => {
    if (isLoading || isLoadingMoreData || !hasMoreData) return;
    setPage((prev) => prev + 1);
    setIsLoadingMoreData(true);
  }, [isLoadingMoreData, isLoading, hasMoreData]);

  useEffect(() => {
    initialized.current = true;
    fetchPaymentHistories();
  }, [fetchPaymentHistories]);

  useEffect(() => {
    if (!scrollListRef?.current || !sentinelRef?.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries?.[0]?.isIntersecting) {
          handleLoadMore();
        }
      },
      {
        root: scrollListRef?.current,
        rootMargin: "0px 0px 20px 0px",
        threshold: 0,
      },
    );
    observer.observe(sentinelRef?.current);
    return () => {
      observer.disconnect();
    };
  }, [isLoadingMoreData, handleLoadMore]);

  return (
    <div className="flex flex-col min-h-0">
      {/* Search container */}
      <div className="flex items-center mt-6 bg-[#F1F5F9] p-1 rounded-md justify-between">
        <div className="flex items-center gap-3 w-[60%]">
          <div className="bg-white rounded-md px-3 border flex items-center h-8 flex-1">
            <input
              className="text-xs font-medium flex-1 outline-none h-full"
              placeholder="Search reference"
              value={searchText}
              onChange={(e) => setSearchText(e?.currentTarget?.value)}
            />
            <SearchIcon className="w-4 h-4" color="gray" />
          </div>
          <DateFilter range={dateRange} setRange={applyDateRange} />
          <PaymentTypeFilter type={type} setType={applyType} />
        </div>
        <button className="flex items-center bg-[#6F6F6F] font-medium text-xs text-white rounded-md px-5 py-2 gap-2">
          <LogOut className="w-4 h-4" />
          Export
        </button>
      </div>
      {/* List container */}
      <div className="flex flex-col mt-5 px-2 min-h-0">
        {/* Header */}
        <div className="grid grid-cols-[1fr_2fr_1fr_1fr_1fr] border-b pb-2 px-2">
          <span className="font-semibold text-xs text-light-black">
            Reference No.
          </span>
          <span className="font-semibold text-xs text-light-black">
            Payment from
          </span>
          <span className="font-semibold text-xs text-light-black">Status</span>
          <span className="font-semibold text-xs text-light-black">Amount</span>
          <span className="font-semibold text-xs text-light-black">Date</span>
        </div>
        <div
          ref={scrollListRef}
          className="flex flex-col overflow-y-scroll min-h-0 no-scrollbar"
        >
          {isLoading ? (
            <LoaderPaymentReceived />
          ) : paymentHistories?.length ? (
            <>
              {paymentHistories?.map((paymentHistory) => {
                return (
                  <NavLink
                    key={paymentHistory?._id}
                    to={`/payment/details/${paymentHistory?._id}/${paymentHistory?.type}`}
                    className="grid grid-cols-[1fr_2fr_1fr_1fr_1fr] px-2 py-4 border-b hover:bg-gray-100 cursor-pointer"
                  >
                    <span className="text-xs text-charcoal">
                      {paymentHistory?.refNo}
                    </span>
                    <span className="text-xs text-charcoal">
                      {paymentHistory?.paidByDetails?.firstName}{" "}
                      {paymentHistory?.paidByDetails?.lastName} -{" "}
                      <span className="italic font-medium text-xss text-secondary">
                        {getEnumKeyFromValue(
                          EnumPaymentHistoryTypes,
                          paymentHistory?.type,
                        )}
                      </span>
                    </span>
                    <span className="text-xs text-[#B3261E]">Paid</span>
                    <span className="text-xs text-charcoal">
                      £ {paymentHistory?.totalAmount}
                    </span>
                    <span className="text-xs text-charcoal">
                      {dayjs(paymentHistory?.updatedAt).format(
                        "DD MM YYYY, hh:mm a",
                      )}
                    </span>
                  </NavLink>
                );
              })}
              {/* Sentinel */}
              <div ref={sentinelRef} className="h-px shrink-0" />
              {isLoadingMoreData ? (
                <div className="flex gap-2 items-center justify-center italic text-xs font-medium text-light-black">
                  <LoadingAnimation color="black" />
                  Loading more data...
                </div>
              ) : null}
            </>
          ) : (
            <div className="flex flex-col justify-center items-center h-96 font-medium text-sm">
              You haven't received any payments yet.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentsSection;
